import type { SeenMedplumClient } from '@seen/core/client'
import { ExtendedOrganization } from '@seen/core/models/organizations/ExtendedOrganization'
import { Participant } from '@seen/core/models/patients/participant'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import { ExtendedRelatedPerson } from '@seen/core/models/relatedPerson'
import type { QueryTypes } from '@seen/core/search'
import { createElement } from 'react'
import type { ComboboxAsyncOption } from '#src/components/core/combobox/ComboboxAsync.tsx'
import { PractitionerNameplate } from '#src/components/nameplates/PractitionerNameplate.tsx'
import { ParticipantOption } from '#src/components/options/ParticipantOption.tsx'

export interface ReferenceOption extends ComboboxAsyncOption<string> {
  // The record's name as plain text, for a reference's `display`.
  display: string
}

// Records of `resourceType` (Patient, Practitioner, Organization or
// RelatedPerson) matching `input`, as options keyed by reference.
export async function loadReferenceOptions({
  medplum,
  resourceType,
  input,
  patientId,
  selected,
}: {
  medplum: SeenMedplumClient
  resourceType: string
  input: string
  patientId: string | undefined
  selected: string[]
}): Promise<ReferenceOption[]> {
  const query = input.trim()
  if (resourceType === 'Patient') {
    const participantQuery: QueryTypes = Participant.baseSearchQuery()
    if (query) {
      Object.assign(participantQuery, Participant.byName(query))
    }
    participantQuery._count = '20'
    const participants = await medplum.searchResourceClass(
      Participant,
      participantQuery
    )
    return participants.flatMap((participant) => {
      const reference = participant.reference.reference
      return reference
        ? [
            {
              value: reference,
              display: participant.name,
              label: createElement(ParticipantOption, {
                participant: participant.reference,
                isSelected: selected.includes(reference),
              }),
              keywords: participant.searchKeywords,
            },
          ]
        : []
    })
  }
  if (resourceType === 'Practitioner') {
    const practitioners = await medplum.searchResourceClass(
      ExtendedPractitioner,
      {
        ...ExtendedPractitioner.allInternalSearchQuery(query || undefined),
        _count: '20',
      }
    )
    return practitioners.flatMap((practitioner) => {
      const reference = practitioner.reference.reference
      return reference
        ? [
            {
              value: reference,
              display: practitioner.name,
              label: createElement(PractitionerNameplate, {
                practitioner: reference,
              }),
              keywords: practitioner.searchKeywords,
            },
          ]
        : []
    })
  }
  if (resourceType === 'Organization') {
    const organizationQuery: QueryTypes = query
      ? ExtendedOrganization.byName(query)
      : {}
    organizationQuery._count = '20'
    const organizations = await medplum.searchResourceClass(
      ExtendedOrganization,
      organizationQuery
    )
    return organizations.flatMap((organization) => {
      const reference = organization.reference.reference
      const name = organization.name ?? 'Organization'
      return reference
        ? [
            {
              value: reference,
              display: name,
              label: createElement('span', null, name),
              keywords: [name],
            },
          ]
        : []
    })
  }
  const personQuery: QueryTypes = { _count: '20' }
  if (patientId) {
    personQuery.patient = `Patient/${patientId}`
  }
  if (query) {
    personQuery.name = query.replace(/,/g, '')
  }
  const people = await medplum.searchResourceClass(
    ExtendedRelatedPerson,
    personQuery
  )
  return people.flatMap((person) => {
    const reference = person.reference.reference
    return reference
      ? [
          {
            value: reference,
            display: person.name,
            label: createElement('span', null, person.name),
            keywords: [person.name],
          },
        ]
      : []
  })
}

export function referenceNoun(resourceType: string): string {
  if (resourceType === 'Patient') {
    return 'participant'
  }
  if (resourceType === 'RelatedPerson') {
    return 'contact'
  }
  if (resourceType === 'Organization') {
    return 'organization'
  }
  return 'staff member'
}
